import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import { useToast } from '../components/Toast';
import { getObjects, updateObject, deleteObject, deleteObjectPhoto, deleteObjectDocument, addObjectPhotos } from '../api/client';
import {
  MapPin, Phone, MessageCircle, Calendar, Users, Check, X, Clipboard,
  Paperclip, Camera, Pencil, RotateCcw, Flame, UserPlus,
  ChevronLeft, ChevronRight, Hammer, IconBadge, FileText,
} from '../components/icons';

// Скоуп-стили раздела: tactile press на карточках/кнопках + guard reduced-motion
const OBJ_CSS = `
.obj-root { --ease: cubic-bezier(.22,.68,0,1); }
.obj-card { transition: transform .16s var(--ease), border-color .25s ease; -webkit-tap-highlight-color: transparent; }
.obj-card:active { transform: scale(.985); }
.obj-root button:active { transform: scale(.97); }
@media (prefers-reduced-motion: reduce) {
  .obj-card, .obj-root button { transition: none; transform: none !important; }
  .obj-root [class*="fadeInUp"] { animation: none !important; }
}
`;

// Инлайн-метка с SVG-иконкой (город, дата, люди)
const Meta = ({ icon, children, color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: color || 'inherit' }}>
    {React.cloneElement(icon, { size: 13 })} {children}
  </span>
);

const API = '';
const TYPES = ['Все', 'Малярка', 'Электрика', 'Сантехника', 'Плитка', 'Фасады', 'Кровля', 'Гипсокартон', 'Полы', 'Демонтаж', 'Наружные работы', 'Под ключ', 'Другое'];

// German cities → main PLZ (Postleitzahl)
const CITY_PLZ = {
  'berlin': '10115', 'hamburg': '20095', 'münchen': '80331', 'munich': '80331', 'köln': '50667',
  'frankfurt': '60311', 'frankfurt am main': '60311', 'stuttgart': '70173', 'düsseldorf': '40210',
  'dortmund': '44135', 'essen': '45127', 'leipzig': '04109', 'bremen': '28195', 'dresden': '01067',
  'hannover': '30159', 'nürnberg': '90402', 'duisburg': '47051', 'bochum': '44787',
  'wuppertal': '42103', 'bielefeld': '33602', 'bonn': '53111', 'münster': '48143',
  'karlsruhe': '76131', 'mannheim': '68161', 'augsburg': '86150', 'wiesbaden': '65183',
  'gelsenkirchen': '45879', 'mönchengladbach': '41061', 'braunschweig': '38100',
  'chemnitz': '09111', 'kiel': '24103', 'aachen': '52062', 'halle': '06108',
  'magdeburg': '39104', 'freiburg': '79098', 'krefeld': '47798', 'lübeck': '23552',
  'oberhausen': '46045', 'erfurt': '99084', 'mainz': '55116', 'rostock': '18055',
  'kassel': '34117', 'hagen': '58095', 'hamm': '59065', 'saarbrücken': '66111',
  'mülheim': '45468', 'potsdam': '14467', 'ludwigshafen': '67059', 'oldenburg': '26121',
  'leverkusen': '51373', 'osnabrück': '49074', 'solingen': '42651', 'heidelberg': '69115',
  'herne': '44623', 'neuss': '41460', 'darmstadt': '64283', 'paderborn': '33098',
  'regensburg': '93047', 'ingolstadt': '85049', 'würzburg': '97070', 'wolfsburg': '38440',
  'ulm': '89073', 'heilbronn': '74072', 'pforzheim': '75172', 'göttingen': '37073',
  'bottrop': '46236', 'trier': '54290', 'recklinghausen': '45657', 'reutlingen': '72764',
  'bremerhaven': '27568', 'koblenz': '56068', 'bergisch gladbach': '51465', 'jena': '07743',
  'remscheid': '42853', 'erlangen': '91052', 'moers': '47441', 'siegen': '57072',
  'hildesheim': '31134', 'salzgitter': '38226', 'cottbus': '03046', 'kaiserslautern': '67655',
  'schwerin': '19053', 'gütersloh': '33330', 'witten': '58452', 'ludwigsburg': '71638',
  'esslingen': '73728', 'hanau': '63450', 'flensburg': '24937', 'iserlohn': '58636',
  'düren': '52349', 'ratingen': '40878', 'lünen': '44532', 'marl': '45768',
  'velbert': '42549', 'minden': '32423', 'konstanz': '78462', 'villingen-schwenningen': '78050',
  'norderstedt': '22846', 'delmenhorst': '27749', 'bamberg': '96047', 'marburg': '35037',
  'lüneburg': '21335', 'arnsberg': '59821', 'landshut': '84028', 'plauen': '08523',
  'aschaffenburg': '63739', 'fulda': '36037', 'rosenheim': '83022', 'weimar': '99423',
  'offenbach': '63065', 'sindelfingen': '71063', 'lippstadt': '59555', 'dormagen': '41539',
  'passau': '94032', 'friedrichshafen': '88045', 'ravensburg': '88212',
};

// Display-name list for autocomplete (capitalize first letter of each word)
const CITY_NAMES = Object.keys(CITY_PLZ).map(k => k.replace(/\b\w/g, c => c.toUpperCase()));

function findPlz(city) {
  if (!city) return '';
  const key = city.trim().toLowerCase();
  return CITY_PLZ[key] || '';
}

function suggestCities(input) {
  if (!input || input.length < 2) return [];
  const q = input.trim().toLowerCase();
  return CITY_NAMES.filter(c => c.toLowerCase().startsWith(q)).slice(0, 5);
}

const statusBadge = (obj) => {
  if (obj.object_type === 'recommendation')
    return { text: 'Рекомендация', Icon: UserPlus, bg: 'rgba(10,132,255,0.12)', color: '#0A84FF' };
  if (obj.urgency === 'urgent' && obj.status === 'active')
    return { text: 'Срочный', Icon: Flame, bg: 'rgba(255,159,10,0.12)', color: '#FF9F0A' };
  if (obj.status === 'active')
    return { text: 'Активный', bg: 'rgba(48,209,88,0.12)', color: 'var(--success)' };
  return { text: 'Закрыт', bg: 'rgba(99,99,102,0.12)', color: 'var(--text-tertiary)' };
};

// Тонированный бейдж статуса (SVG вместо эмодзи)
const StatusBadge = ({ badge, size = 11, pad = '2px 8px', radius = 6 }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: size, fontWeight: 600,
    padding: pad, borderRadius: radius, background: badge.bg, color: badge.color,
  }}>
    {badge.Icon && <badge.Icon size={size + 1} />}{badge.text}
  </span>
);

const inputStyle = {
  width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)',
  borderRadius: 10, padding: '10px 12px', fontSize: 15, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

const isPhone = (str) => /^\+?\d[\d\s\-()]{6,}$/.test(str?.trim());
const isTelegram = (str) => /^@/.test(str?.trim());

// Parse recommendation fields from description
function parseRecDescription(desc) {
  if (!desc) return { telegram: '', comment: '' };
  const lines = desc.split('\n').filter(Boolean);
  let telegram = '';
  const commentLines = [];
  lines.forEach(l => {
    if (l.trim().startsWith('@')) telegram = l.trim();
    else commentLines.push(l);
  });
  return { telegram, comment: commentLines.join('\n') };
}

export default function ObjectsPage({ onNavigate }) {
  const { toast, toastError } = useToast();
  const [objects, setObjects] = useState([]);
  const [counts, setCounts] = useState({ active: 0, closed: 0 });
  const [filter, setFilter] = useState('Все');
  const [showForm, setShowForm] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showRecForm, setShowRecForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editingObj, setEditingObj] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [docs, setDocs] = useState([]); // прикреплённые PDF (File)
  // Редактирование: уже загруженные файлы объекта; крестик убирает из списка,
  // фактическое удаление на сервере — только при «Сохранить изменения»
  const [existingPhotos, setExistingPhotos] = useState([]); // [{id, file_path}]
  const [existingDocs, setExistingDocs] = useState([]); // [{id, file_name, ...}]
  const [removedPhotoIds, setRemovedPhotoIds] = useState([]);
  const [removedDocIds, setRemovedDocIds] = useState([]);
  const fileRef = useRef(null);
  const touchStartY = useRef(0);
  const modalRef = useRef(null);
  const [customType, setCustomType] = useState('');
  const [budgetMode, setBudgetMode] = useState('amount'); // amount, negotiable, discuss
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const lbTouchX = useRef(0);
  const [form, setForm] = useState({ types: [], city: '', district: '', title: '', description: '', budget: '', workers_needed: 1, urgency: 'normal', start_date: '', start_date_exact: '', contact: '' });
  const [recForm, setRecForm] = useState({ types: [], city: '', contactName: '', phone: '', telegram: '', comment: '' });
  const [recCitySuggestions, setRecCitySuggestions] = useState([]);

  const load = useCallback(() => {
    const params = filter !== 'Все' ? `type=${encodeURIComponent(filter)}` : '';
    getObjects(params).then(data => {
      setObjects(data.objects || []);
      setCounts(data.counts || { active: 0, closed: 0 });
    }).catch(console.error);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Clean up dropdowns when returning from file picker / app regains focus
  useEffect(() => {
    const handleFocus = () => { setCitySuggestions([]); setRecCitySuggestions([]); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const FORM_TYPES = TYPES.filter(t => t !== 'Все');

  const toggleType = (arr, t) => arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t];

  const resetForm = () => {
    setForm({ types: [], city: '', district: '', title: '', description: '', budget: '', workers_needed: 1, urgency: 'normal', start_date: '', start_date_exact: '', contact: '' });
    setCustomType('');
    setBudgetMode('amount');
    setEditingObj(null);
    setPhotos([]);
    setPreviews([]);
    setDocs([]);
    setExistingPhotos([]);
    setExistingDocs([]);
    setRemovedPhotoIds([]);
    setRemovedDocIds([]);
  };

  const resetRecForm = () => {
    setRecForm({ types: [], city: '', contactName: '', phone: '', telegram: '', comment: '' });
    setFormErrors({});
  };

  const openCreate = () => { setShowChoiceModal(true); };

  const openEdit = (obj) => {
    const parsedTypes = (obj.type || '').split(',').map(s => s.trim()).filter(Boolean);
    const knownTypes = parsedTypes.filter(t => FORM_TYPES.includes(t));
    const unknownTypes = parsedTypes.filter(t => !FORM_TYPES.includes(t) && t !== 'Другое');
    const editTypes = unknownTypes.length > 0 ? [...knownTypes, 'Другое'] : knownTypes;
    if (unknownTypes.length > 0) setCustomType(unknownTypes.join(', '));
    setForm({
      types: editTypes.length > 0 ? editTypes : ['Малярка'], city: obj.city || '', district: obj.district || '', title: obj.title || '',
      description: obj.description || '', budget: obj.budget || '',
      workers_needed: obj.workers_needed || 1, urgency: obj.urgency || 'normal',
      start_date: obj.start_date || '', start_date_exact: '', contact: obj.contact || '',
    });
    setEditingObj(obj);
    setShowDetail(null);
    setPhotos([]);
    setPreviews([]);
    setDocs([]);
    setExistingPhotos(obj.photo_items || []);
    setExistingDocs(obj.documents || []);
    setRemovedPhotoIds([]);
    setRemovedDocIds([]);
    setShowForm(true);
  };

  const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10MB — совпадает с лимитом multer на бэке

  const handlePhotos = async (e) => {
    const input = e.target;
    // Close any open dropdowns after returning from file picker
    setCitySuggestions([]);
    setRecCitySuggestions([]);
    const picked = Array.from(input.files).slice(0, 10 - photos.length - docs.length - existingPhotos.length - existingDocs.length);
    if (picked.length === 0) return;
    const tooBig = picked.find(f => f.size > (f.type === 'application/pdf' ? MAX_PDF_SIZE : MAX_PHOTO_SIZE));
    if (tooBig) {
      toastError(`Файл "${tooBig.name}" слишком большой (макс. ${tooBig.type === 'application/pdf' ? 10 : 5} МБ)`);
      input.value = '';
      return;
    }
    // TG WebView может отозвать доступ к выбранному файлу (особенно PDF из «Файлов»/Drive)
    // к моменту отправки формы — fetch тогда падает с «Failed to fetch», не уходя в сеть.
    // Поэтому читаем байты в память сразу при выборе и дальше работаем с копией.
    let files;
    try {
      files = await Promise.all(picked.map(async f =>
        new File([await f.arrayBuffer()], f.name, { type: f.type })
      ));
    } catch (err) {
      toastError('Не удалось прочитать файл — выберите его ещё раз');
      input.value = '';
      return;
    }
    const pdfs = files.filter(f => f.type === 'application/pdf');
    const images = files.filter(f => f.type !== 'application/pdf');
    setDocs(prev => [...prev, ...pdfs]);
    setPhotos(prev => [...prev, ...images]);
    images.forEach(f => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
    input.value = '';
    // iOS fix: blur file input and force layout recalc after native picker closes
    input.blur();
    setTimeout(() => { window.dispatchEvent(new Event('resize')); }, 100);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const removeDoc = (idx) => setDocs(prev => prev.filter((_, i) => i !== idx));

  const removeExistingPhoto = (photo) => {
    setExistingPhotos(prev => prev.filter(p => p.id !== photo.id));
    setRemovedPhotoIds(prev => [...prev, photo.id]);
  };

  const removeExistingDoc = (doc) => {
    setExistingDocs(prev => prev.filter(d => d.id !== doc.id));
    setRemovedDocIds(prev => [...prev, doc.id]);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const errors = {};
    if (form.types.length === 0) errors.types = true;
    if (!form.city.trim()) errors.city = true;
    if (!form.district.trim()) errors.district = true;
    if (!form.title.trim()) errors.title = true;
    if (!form.contact.trim()) errors.contact = true;
    if (form.types.includes('Другое') && !customType.trim()) errors.customType = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setSubmitting(true);
    // Build type string from selected types
    const typesList = form.types.map(t => t === 'Другое' ? customType.trim() : t).filter(Boolean);
    const submitForm = { ...form, type: typesList.join(', '), workers_needed: parseInt(form.workers_needed) || 1 };
    delete submitForm.types;
    if (budgetMode === 'negotiable') { submitForm.budget = -1; submitForm.budget_label = 'Договорная'; }
    else if (budgetMode === 'discuss') { submitForm.budget = -2; submitForm.budget_label = 'Обсуждается'; }
    if (submitForm.start_date === 'Точная дата' && submitForm.start_date_exact) {
      submitForm.start_date = new Date(submitForm.start_date_exact).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    delete submitForm.start_date_exact;
    try {
      if (editingObj) {
        const data = { ...submitForm, budget: submitForm.budget ? parseFloat(submitForm.budget) : null };
        await updateObject(editingObj.id, data);
        // Удаляем помеченные крестиком файлы; одна неудача не блокирует остальные
        for (const pid of removedPhotoIds) {
          try { await deleteObjectPhoto(editingObj.id, pid); } catch (e) { console.error('delete photo failed:', e); }
        }
        for (const did of removedDocIds) {
          try { await deleteObjectDocument(editingObj.id, did); } catch (e) { console.error('delete document failed:', e); }
        }
        if (photos.length > 0 || docs.length > 0) {
          const fd = new FormData();
          photos.forEach(f => fd.append('photos', f));
          docs.forEach(f => fd.append('photos', f));
          await addObjectPhotos(editingObj.id, fd);
        }
      } else {
        const formData = new FormData();
        Object.keys(submitForm).forEach(k => { if (submitForm[k] !== '' && submitForm[k] !== null && submitForm[k] !== undefined) formData.append(k, submitForm[k]); });
        formData.append('object_type', 'object');
        photos.forEach(f => formData.append('photos', f));
        docs.forEach(f => formData.append('photos', f));
        const initData = window.Telegram?.WebApp?.initData;
        const headers = {};
        if (initData) headers['X-Telegram-InitData'] = initData;
        else headers['X-Dev-User'] = '100001';
        const res = await fetch(API + '/api/objects', { method: 'POST', headers, body: formData });
        if (!res.ok) throw new Error('Ошибка сервера');
      }
      toast(editingObj ? 'Объект обновлён' : 'Объект создан');
      setShowForm(false);
      resetForm();
      setTimeout(load, 300);
    } catch (e) {
      const msg = /failed to fetch|load failed/i.test(e.message || '')
        ? 'Сбой сети при отправке. Проверьте соединение и попробуйте ещё раз'
        : (e.message || 'Не удалось сохранить объект');
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecSubmit = async () => {
    if (submitting) return;
    const errors = {};
    if (recForm.types.length === 0) errors.recType = true;
    if (!recForm.city.trim()) errors.recCity = true;
    if (!recForm.contactName.trim()) errors.recName = true;
    if (!recForm.phone.trim()) errors.recPhone = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    const descParts = [];
    if (recForm.telegram.trim()) descParts.push(recForm.telegram.trim());
    if (recForm.comment.trim()) descParts.push(recForm.comment.trim());

    const formData = new FormData();
    formData.append('type', recForm.types.join(', '));
    formData.append('city', recForm.city);
    formData.append('title', `Рекомендация: ${recForm.contactName.trim()}`);
    formData.append('contact', recForm.phone.trim());
    formData.append('description', descParts.join('\n'));
    formData.append('object_type', 'recommendation');

    try {
      const initData = window.Telegram?.WebApp?.initData;
      const headers = {};
      if (initData) headers['X-Telegram-InitData'] = initData;
      else headers['X-Dev-User'] = '100001';
      const res = await fetch(API + '/api/objects', { method: 'POST', headers, body: formData });
      if (!res.ok) throw new Error('Ошибка сервера');
      toast('Рекомендация отправлена');
      setShowRecForm(false);
      resetRecForm();
      setTimeout(load, 300);
    } catch (e) {
      toastError(e.message || 'Не удалось отправить рекомендацию');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (obj) => {
    try {
      const newStatus = obj.status === 'active' ? 'closed' : 'active';
      await updateObject(obj.id, { status: newStatus });
      toast(newStatus === 'closed' ? 'Объект закрыт' : 'Объект открыт');
      setShowDetail(null);
      load();
    } catch (e) {
      toastError('Не удалось изменить статус');
    }
  };

  // Swipe down to close modal
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 80 && modalRef.current?.scrollTop <= 0) setShowDetail(null);
  };

  const ContactLink = ({ value }) => {
    if (!value) return null;
    const v = value.trim();
    if (isPhone(v)) return <a href={`tel:${v.replace(/[\s\-()]/g, '')}`} style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}><Meta icon={<Phone />}>{v}</Meta></a>;
    if (isTelegram(v)) return <a href={`https://t.me/${v.slice(1)}`} target="_blank" rel="noreferrer" style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}><Meta icon={<MessageCircle />}>{v}</Meta></a>;
    return <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{v}</span>;
  };

  // Render recommendation card (compact)
  const renderRecCard = (obj, i) => {
    const badge = statusBadge(obj);
    const contactName = (obj.title || '').replace(/^Рекомендация:\s*/, '');
    const rec = parseRecDescription(obj.description);
    return (
      <div key={obj.id} onClick={() => setShowDetail(obj)} className="obj-card" style={{
        background: 'var(--bg-card)', borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border)', opacity: obj.status === 'closed' ? 0.5 : 1,
        animation: `fadeInUp 0.3s ease ${0.05 * i}s both`, cursor: 'pointer',
      }}>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <StatusBadge badge={badge} />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{obj.type}</span>
            </div>
            <span style={{ fontSize: 12, color: '#0A84FF' }}><Meta icon={<MapPin />}>{obj.city}</Meta></span>
          </div>

          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{contactName}</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: rec.comment ? 8 : 0 }}>
            <a href={`tel:${obj.contact?.replace(/[\s\-()]/g, '')}`} onClick={e => e.stopPropagation()} style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}><Meta icon={<Phone />}>{obj.contact}</Meta></a>
            {rec.telegram && (
              <a href={`https://t.me/${rec.telegram.replace('@', '')}`} onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer" style={{ color: '#0A84FF', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}><Meta icon={<MessageCircle />}>{rec.telegram}</Meta></a>
            )}
          </div>

          {rec.comment && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {rec.comment}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                {obj.author_initials || '??'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{obj.author_name || 'Аноним'}</span>
            </div>
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => handleToggleStatus(obj)} style={{ background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                  {obj.status === 'active' ? <Check size={13} /> : <RotateCcw size={13} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const openDocument = (d) => {
    const url = window.location.origin + d.file_path;
    if (window.Telegram?.WebApp?.openLink) window.Telegram.WebApp.openLink(url);
    else window.open(url, '_blank');
  };

  // Render regular object card
  const renderObjectCard = (obj, i) => {
    const badge = statusBadge(obj);
    const thumb = obj.photos?.[0];
    const docWithThumb = !thumb ? obj.documents?.find(d => d.thumb_path) : null;
    return (
      <div key={obj.id} onClick={() => setShowDetail(obj)} className="obj-card" style={{
        background: 'var(--bg-card)', borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border)', opacity: obj.status === 'closed' ? 0.5 : 1,
        animation: `fadeInUp 0.3s ease ${0.05 * i}s both`, cursor: 'pointer',
      }}>
        {/* Photo thumbnail */}
        {thumb && (
          <div style={{ width: '100%', maxHeight: 200, overflow: 'hidden', position: 'relative', background: '#111' }}>
            <img src={thumb} alt="" style={{ width: '100%', display: 'block' }} />
            {obj.photos.length > 1 && (
              <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Camera size={12} /> {obj.photos.length}
              </div>
            )}
          </div>
        )}

        {/* PDF вместо миниатюры — объект без фото: превью первой страницы как фото, тап по плашке открывает файл */}
        {!thumb && docWithThumb && (
          <div style={{ width: '100%', maxHeight: 200, overflow: 'hidden', position: 'relative', background: '#fff' }}>
            <img src={docWithThumb.thumb_path} alt="" style={{ width: '100%', display: 'block', objectFit: 'cover', objectPosition: 'top' }} />
            <div onClick={e => { e.stopPropagation(); openDocument(docWithThumb); }} style={{
              position: 'absolute', bottom: 8, left: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 6,
              padding: '4px 8px', fontSize: 11, fontWeight: 600, color: '#fff',
              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
            }}>
              <FileText size={12} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docWithThumb.file_name}</span>
              {obj.documents.length > 1 && <span style={{ marginLeft: 'auto', flexShrink: 0 }}>×{obj.documents.length}</span>}
            </div>
          </div>
        )}
        {/* Fallback: превью не сгенерилось — строчный список документов */}
        {!thumb && !docWithThumb && obj.documents?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderBottom: '1px solid var(--border)' }}>
            {obj.documents.slice(0, 2).map(d => (
              <div key={d.id} onClick={e => { e.stopPropagation(); openDocument(d); }} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                background: 'var(--bg-primary)', cursor: 'pointer',
              }}>
                <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><FileText size={16} /></span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.file_name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>PDF</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <StatusBadge badge={badge} />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{obj.type}</span>
              {obj.documents?.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <FileText size={11} /> PDF{obj.documents.length > 1 ? ` ×${obj.documents.length}` : ''}
                </span>
              )}
            </div>
            <span onClick={e => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/${encodeURIComponent(obj.city + (obj.district ? ' ' + obj.district : '') + ', Germany')}`); }} style={{ fontSize: 12, color: '#0A84FF', cursor: 'pointer' }}><Meta icon={<MapPin />}>{obj.city}{obj.district ? ` ${obj.district}` : ''}</Meta></span>
          </div>

          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>{obj.title}</div>

          {obj.description && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {obj.description}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
              {obj.start_date && <Meta icon={<Calendar />}>{obj.start_date}</Meta>}
              {obj.workers_needed > 1 && <Meta icon={<Users />}>{obj.workers_needed} чел</Meta>}
            </div>
            {(obj.budget > 0 || obj.budget_label) && <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)' }}>{obj.budget_label || `€${obj.budget.toLocaleString()}`}</span>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                {obj.author_initials || '??'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{obj.author_name || 'Аноним'}</span>
            </div>
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => openEdit(obj)} style={{ background: 'var(--bg-elevated)', color: 'var(--accent)', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><Pencil size={13} /></button>
                <button onClick={() => handleToggleStatus(obj)} style={{ background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                  {obj.status === 'active' ? <Check size={13} /> : <RotateCcw size={13} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render recommendation detail modal
  const renderRecDetail = (obj) => {
    const contactName = (obj.title || '').replace(/^Рекомендация:\s*/, '');
    const rec = parseRecDescription(obj.description);
    return (
      <div onClick={() => setShowDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
        <div ref={modalRef} onClick={e => e.stopPropagation()}
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
          style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            width: '100%', maxHeight: '92vh', overflowY: 'auto', animation: 'slideUp 0.3s ease',
          }}>

          <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
            <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, opacity: 0.5 }}></div>
            </div>
            <div style={{ width: 50 }}></div>
          </div>

          <div style={{ padding: '12px 20px calc(40px + var(--tg-safe-bottom, 0px))' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <StatusBadge badge={{ text: 'Рекомендация', Icon: UserPlus, bg: 'rgba(10,132,255,0.12)', color: '#0A84FF' }} size={12} pad="3px 10px" radius={8} />
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{obj.type}</span>
            </div>

            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{contactName}</h2>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}><Meta icon={<MapPin />}>{obj.city}</Meta></div>

            {/* Contact actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {obj.contact && (
                <a href={`tel:${obj.contact.replace(/[\s\-()]/g, '')}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'var(--accent)', borderRadius: 12, padding: '14px',
                  color: '#0D0D0D', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                }}><Phone size={17} /> Позвонить {obj.contact}</a>
              )}
              {rec.telegram && (
                <a href={`https://t.me/${rec.telegram.replace('@', '')}`} target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: '#0088cc', borderRadius: 12, padding: '14px',
                  color: 'white', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                }}><MessageCircle size={17} /> Написать в Telegram {rec.telegram}</a>
              )}
            </div>

            {rec.comment && (
              <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8 }}>КОММЕНТАРИЙ</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{rec.comment}</div>
              </div>
            )}

            {/* Who recommended */}
            <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 12 }}>ПОРЕКОМЕНДОВАЛ</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>
                  {obj.author_initials || '??'}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{obj.author_name || 'Аноним'}</div>
                </div>
              </div>
            </div>

            {/* Author actions */}
            {obj.is_author && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button onClick={() => handleToggleStatus(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  {obj.status === 'active' ? <><Check size={15} /> Завершить</> : <><RotateCcw size={15} /> Открыть</>}
                </button>
              </div>
            )}

            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
              Опубликовано: {new Date(obj.created_at).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="obj-root">
      <style>{OBJ_CSS}</style>
      <Header title="Объекты" left={onNavigate && (
        <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
      )} right={
        <button onClick={openCreate} style={{ background: 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Предложить</button>
      } />

      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Dashboard */}
        <div style={{ display: 'flex', gap: 8, animation: 'fadeInUp 0.3s ease both' }}>
          <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 12, padding: '10px 14px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconBadge size={32} iconSize={16}><Hammer /></IconBadge>
            <div>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{counts.active}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>активных</span>
            </div>
          </div>
          <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: 12, padding: '10px 14px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconBadge accent={false} size={32} iconSize={16}><Check /></IconBadge>
            <div>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-tertiary)' }}>{counts.closed}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>завершённых</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{
              background: filter === t ? 'var(--accent)' : 'var(--bg-card)',
              color: filter === t ? '#0D0D0D' : 'var(--text-secondary)',
              border: '1px solid ' + (filter === t ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}>{t}</button>
          ))}
        </div>

        {/* Objects */}
        {objects.map((obj, i) => (
          obj.object_type === 'recommendation' ? renderRecCard(obj, i) : renderObjectCard(obj, i)
        ))}

        {objects.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <IconBadge accent={false} size={56} iconSize={26}><Clipboard /></IconBadge>
            </div>
            <div>Нет объектов</div>
          </div>
        )}
      </div>


      {/* Choice Modal */}
      {showChoiceModal && (
        <div onClick={() => setShowChoiceModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px 40px', width: '100%', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 16px' }}></div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>Что вы хотите сделать?</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div onClick={() => { setShowChoiceModal(false); resetForm(); setShowForm(true); }} className="obj-card" style={{
                background: 'var(--bg-primary)', borderRadius: 16, padding: 20,
                border: '1px solid var(--border)', cursor: 'pointer',
              }}>
                <div style={{ marginBottom: 10 }}><IconBadge size={44} iconSize={22}><Clipboard /></IconBadge></div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Разместить объект</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>Подробное описание с фото, бюджетом и деталями</div>
              </div>

              <div onClick={() => { setShowChoiceModal(false); resetRecForm(); setShowRecForm(true); }} className="obj-card" style={{
                background: 'var(--bg-primary)', borderRadius: 16, padding: 20,
                border: '1px solid var(--border)', cursor: 'pointer',
              }}>
                <div style={{ marginBottom: 10 }}><IconBadge size={44} iconSize={22}><UserPlus /></IconBadge></div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Порекомендовать контакт</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>Быстро поделиться контактом клиента или работодателя</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (() => {
        const obj = showDetail;
        if (obj.object_type === 'recommendation') return renderRecDetail(obj);
        const badge = statusBadge(obj);
        const hasPhotos = obj.photos?.length > 0;
        return (
          <div onClick={() => setShowDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
            <div ref={modalRef} onClick={e => e.stopPropagation()}
              onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
              style={{
                background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
                width: '100%', maxHeight: '92vh', overflowY: 'auto', animation: 'slideUp 0.3s ease',
              }}>

              {/* Top bar: back button + swipe handle */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
                <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Назад</button>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, opacity: 0.5 }}></div>
                </div>
                <div style={{ width: 50 }}></div>
              </div>

              {/* Photo gallery */}
              {hasPhotos && (
                <div style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', marginTop: 8 }}>
                  {obj.photos.map((p, idx) => (
                    <div key={idx} onClick={() => setLightbox({ photos: obj.photos, index: idx })} style={{ minWidth: '100%', scrollSnapAlign: 'start', background: '#111', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                      <img src={p} alt="" style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }} />
                    </div>
                  ))}
                </div>
              )}
              {hasPhotos && obj.photos.length > 1 && (
                <div style={{ textAlign: 'center', padding: '6px 0', fontSize: 11, color: 'var(--text-tertiary)' }}>
                  ← свайп для просмотра • {obj.photos.length} фото →
                </div>
              )}

              <div style={{ padding: '12px 20px calc(40px + var(--tg-safe-bottom, 0px))' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusBadge badge={badge} size={12} pad="3px 10px" radius={8} />
                    <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{obj.type}</span>
                  </div>
                  <span onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(obj.city + (obj.district ? ' ' + obj.district : '') + ', Germany')}`)} style={{ fontSize: 13, color: '#0A84FF', cursor: 'pointer', textDecoration: 'underline' }}><Meta icon={<MapPin />}>{obj.city}{obj.district ? `, ${obj.district}` : ''}</Meta></span>
                </div>

                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, lineHeight: 1.3 }}>{obj.title}</h2>

                {obj.description && (
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 16, whiteSpace: 'pre-wrap' }}>{obj.description}</div>
                )}

                {obj.documents?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                    {obj.documents.map(d => (
                      <div key={d.id} onClick={() => openDocument(d)} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
                      }}>
                        {d.thumb_path ? (
                          <img src={d.thumb_path} alt="" style={{ width: 36, height: 46, objectFit: 'cover', objectPosition: 'top', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', flexShrink: 0 }} />
                        ) : (
                          <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><FileText size={16} /></span>
                        )}
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.file_name}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>PDF</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {(obj.budget > 0 || obj.budget_label) && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Бюджет</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{obj.budget_label || `€${obj.budget.toLocaleString()}`}</div>
                    </div>
                  )}
                  {obj.workers_needed > 0 && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Нужно людей</div>
                      <div style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><Users size={17} /> {obj.workers_needed}</div>
                    </div>
                  )}
                  {obj.start_date && (
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Начало работ</div>
                      <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={15} /> {obj.start_date}</div>
                    </div>
                  )}
                  {obj.urgency === 'urgent' && (
                    <div style={{ background: 'rgba(255,159,10,0.08)', borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>Срочность</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#FF9F0A', display: 'flex', alignItems: 'center', gap: 6 }}><Flame size={15} /> Срочно</div>
                    </div>
                  )}
                </div>

                {/* Contacts */}
                <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 12 }}>КОНТАКТЫ</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>
                      {obj.author_initials || '??'}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{obj.author_name || 'Аноним'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Автор объекта</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {obj.contact && (
                      <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '10px 14px' }}>
                        <ContactLink value={obj.contact} />
                      </div>
                    )}
                    {obj.author_username && (
                      <a href={`https://t.me/${obj.author_username}`} target="_blank" rel="noreferrer" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: '#0088cc', borderRadius: 10, padding: '10px 14px',
                        color: 'white', fontSize: 14, fontWeight: 600, textDecoration: 'none',
                      }}><MessageCircle size={16} /> Написать в Telegram</a>
                    )}
                    {obj.author_telegram_id && !obj.author_username && (
                      <a href={`tg://user?id=${obj.author_telegram_id}`} target="_blank" rel="noreferrer" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: '#0088cc', borderRadius: 10, padding: '10px 14px',
                        color: 'white', fontSize: 14, fontWeight: 600, textDecoration: 'none',
                      }}><MessageCircle size={16} /> Написать в Telegram</a>
                    )}
                  </div>
                </div>

                {/* Author actions */}
                {obj.is_author && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <button onClick={() => openEdit(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Pencil size={15} /> Изменить</button>
                    <button onClick={() => handleToggleStatus(obj)} style={{ flex: 1, background: 'var(--bg-elevated)', color: obj.status === 'active' ? 'var(--success)' : '#0A84FF', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {obj.status === 'active' ? <><Check size={15} /> Завершить</> : <><RotateCcw size={15} /> Открыть</>}
                    </button>
                  </div>
                )}

                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                  Опубликовано: {new Date(obj.created_at).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create/Edit Object Modal */}
      {showForm && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); resetForm(); } }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px calc(40px + var(--tg-safe-bottom, 0px))', width: '100%', maxHeight: '92vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 12px' }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editingObj ? 'Редактировать объект' : 'Новый объект'}</h3>
              <div onClick={() => { setShowForm(false); resetForm(); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }}><X size={16} /></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div data-error={formErrors.types ? 'true' : undefined}>
                <label style={{ fontSize: 12, color: formErrors.types ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Вид работ (можно несколько) <span style={{ color: 'var(--error)' }}>*</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FORM_TYPES.map(t => {
                    const sel = form.types.includes(t);
                    return (
                      <div key={t} onClick={() => { setForm(f => ({ ...f, types: toggleType(f.types, t) })); setFormErrors(p => ({ ...p, types: false })); if (t === 'Другое' && !sel) {} else if (t === 'Другое' && sel) setCustomType(''); }} style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: sel ? 'var(--accent-dim)' : 'var(--bg-primary)',
                        color: sel ? 'var(--accent)' : 'var(--text-secondary)',
                        border: '1px solid ' + (sel ? 'var(--accent)' : formErrors.types ? 'var(--error)' : 'var(--border)'),
                      }} >{sel && <Check size={12} />}{t}</div>
                    );
                  })}
                </div>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: form.types.includes('Другое') ? 70 : 0, opacity: form.types.includes('Другое') ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease' }}>
                <div>
                  <label style={{ fontSize: 12, color: formErrors.customType ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Укажите вид работ <span style={{ color: 'var(--error)' }}>*</span></label>
                  <input value={customType} onChange={e => { setCustomType(e.target.value); setFormErrors(p => ({ ...p, customType: false })); }} placeholder="Например: Штукатурка, Ландшафт..." style={{ ...inputStyle, borderColor: formErrors.customType ? 'var(--error)' : undefined }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ position: 'relative' }} data-error={formErrors.city ? 'true' : undefined}>
                  <label style={{ fontSize: 12, color: formErrors.city ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Город <span style={{ color: 'var(--error)' }}>*</span></label>
                  <input value={form.city} onChange={e => { const v = e.target.value; const plz = findPlz(v); setForm(f => ({ ...f, city: v, ...(plz ? { district: plz } : {}) })); setFormErrors(p => ({ ...p, city: false, ...(plz ? { district: false } : {}) })); setCitySuggestions(suggestCities(v)); }} onBlur={() => setTimeout(() => setCitySuggestions([]), 150)} placeholder="Berlin, München..." style={{ ...inputStyle, borderColor: formErrors.city ? 'var(--error)' : undefined }} />
                  {citySuggestions.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: 'var(--bg-card)', border: '1px solid var(--border)',
                      borderRadius: 10, marginTop: 4, overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    }}>
                      {citySuggestions.map(c => (
                        <div key={c} onPointerDown={(e) => {
                          e.preventDefault();
                          const plz = findPlz(c);
                          setForm(f => ({ ...f, city: c, ...(plz ? { district: plz } : {}) }));
                          setFormErrors(p => ({ ...p, city: false, ...(plz ? { district: false } : {}) }));
                          setCitySuggestions([]);
                        }} style={{
                          padding: '10px 14px', fontSize: 14, color: 'var(--text-primary)',
                          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          borderBottom: '1px solid var(--border)',
                        }}>
                          <span>{c}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{findPlz(c)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: formErrors.district ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>PLZ <span style={{ color: 'var(--error)' }}>*</span></label>
                  <input value={form.district} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, district: v })); setFormErrors(p => ({ ...p, district: false })); }} placeholder="10115" style={{ ...inputStyle, borderColor: formErrors.district ? 'var(--error)' : undefined }} maxLength={10} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: formErrors.title ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Название <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={form.title} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, title: v })); setFormErrors(p => ({ ...p, title: false })); }} placeholder="Маляр на объект — квартира 85м²" style={{ ...inputStyle, borderColor: formErrors.title ? 'var(--error)' : undefined }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Описание</label>
                <textarea value={form.description} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, description: v })); }} placeholder="Подробности..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Бюджет</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: budgetMode === 'amount' ? 8 : 0 }}>
                  {[
                    { id: 'amount', label: 'Сумма' },
                    { id: 'discuss', label: 'Обсуждается' },
                  ].map(m => (
                    <div key={m.id} onClick={() => { setBudgetMode(m.id); if (m.id !== 'amount') setForm(f => ({ ...f, budget: '' })); }} style={{
                      flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      background: budgetMode === m.id ? 'var(--accent-dim)' : 'var(--bg-primary)',
                      color: budgetMode === m.id ? 'var(--accent)' : 'var(--text-secondary)',
                      border: '1px solid ' + (budgetMode === m.id ? 'var(--accent)' : 'var(--border)'),
                    }}>{m.label}</div>
                  ))}
                </div>
                <div style={{ overflow: 'hidden', maxHeight: budgetMode === 'amount' ? 50 : 0, opacity: budgetMode === 'amount' ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease', marginTop: budgetMode === 'amount' ? 8 : 0 }}>
                  <input type="number" inputMode="numeric" value={form.budget} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, budget: v })); }} placeholder="Сумма в €" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Людей нужно</label>
                <input type="number" inputMode="numeric" value={form.workers_needed} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, workers_needed: v === '' ? '' : Math.max(1, parseInt(v) || 1) })); }} min="1" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Начало работ</label>
                <select value={form.start_date} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, start_date: v, start_date_exact: '' })); }} style={inputStyle}>
                  <option value="">Выберите</option>
                  <option value="Срочно">Срочно</option>
                  <option value="Эта неделя">Эта неделя</option>
                  <option value="Следующая неделя">Следующая неделя</option>
                  <option value="Этот месяц">Этот месяц</option>
                  <option value="Гибко">Гибко</option>
                  <option value="Точная дата">Точная дата</option>
                </select>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: form.start_date === 'Точная дата' ? 70 : 0, opacity: form.start_date === 'Точная дата' ? 1 : 0, transition: 'max-height 0.2s ease, opacity 0.2s ease' }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Выберите дату</label>
                  <input type="date" value={form.start_date_exact} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, start_date_exact: v })); }} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: formErrors.contact ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Контакт <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={form.contact} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, contact: v })); setFormErrors(p => ({ ...p, contact: false })); }} placeholder="+49 176 12345678 или @username" style={{ ...inputStyle, borderColor: formErrors.contact ? 'var(--error)' : undefined }} />
              </div>

              {/* Photo upload */}
              {(
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Фото и PDF-документы (до 10)</label>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple onChange={handlePhotos} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {existingPhotos.map((p) => (
                      <div key={'ex' + p.id} style={{ width: 64, height: 64, borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                        <img src={p.file_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div onClick={() => removeExistingPhoto(p)} style={{
                          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}><X size={10} /></div>
                      </div>
                    ))}
                    {existingDocs.map((d) => (
                      <div key={'exdoc' + d.id} style={{
                        width: 64, height: 64, borderRadius: 10, position: 'relative',
                        background: 'var(--bg-primary)', border: '1px solid var(--border)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 3, padding: '4px 5px', boxSizing: 'border-box',
                      }}>
                        <span style={{ color: 'var(--accent)', display: 'flex' }}><FileText size={22} /></span>
                        <span style={{
                          fontSize: 8, lineHeight: '10px', color: 'var(--text-secondary)', maxWidth: '100%',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{d.file_name}</span>
                        <div onClick={() => removeExistingDoc(d)} style={{
                          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}><X size={10} /></div>
                      </div>
                    ))}
                    {previews.map((src, idx) => (
                      <div key={idx} style={{ width: 64, height: 64, borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div onClick={() => removePhoto(idx)} style={{
                          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}><X size={10} /></div>
                      </div>
                    ))}
                    {docs.map((f, idx) => (
                      <div key={'doc' + idx} style={{
                        width: 64, height: 64, borderRadius: 10, position: 'relative',
                        background: 'var(--bg-primary)', border: '1px solid var(--border)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 3, padding: '4px 5px', boxSizing: 'border-box',
                      }}>
                        <span style={{ color: 'var(--accent)', display: 'flex' }}><FileText size={22} /></span>
                        <span style={{
                          fontSize: 8, lineHeight: '10px', color: 'var(--text-secondary)', maxWidth: '100%',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{f.name}</span>
                        <div onClick={() => removeDoc(idx)} style={{
                          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}><X size={10} /></div>
                      </div>
                    ))}
                    {existingPhotos.length + existingDocs.length + photos.length + docs.length < 10 && (
                      <div onClick={() => { setCitySuggestions([]); document.activeElement?.blur(); setTimeout(() => fileRef.current?.click(), 50); }} style={{
                        width: 64, height: 64, borderRadius: 10, border: '2px dashed var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 24, color: 'var(--text-tertiary)', cursor: 'pointer',
                      }}><Paperclip size={22} /></div>
                    )}
                  </div>
                </div>
              )}

              <button disabled={submitting} onClick={handleSubmit} style={{
                background: submitting ? 'var(--text-tertiary)' : 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 12,
                padding: '14px', fontSize: 16, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', marginTop: 8,
                opacity: submitting ? 0.6 : 1, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
              }}>{submitting ? 'Отправка...' : (editingObj ? 'Сохранить изменения' : 'Опубликовать')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Form Modal */}
      {showRecForm && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setShowRecForm(false); resetRecForm(); } }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
            padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{ width: 36, height: 4, background: 'var(--text-tertiary)', borderRadius: 2, margin: '0 auto 12px' }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Порекомендовать контакт</h3>
              <div onClick={() => { setShowRecForm(false); resetRecForm(); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }}><X size={16} /></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: formErrors.recType ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Вид работ (можно несколько) <span style={{ color: 'var(--error)' }}>*</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FORM_TYPES.filter(t => t !== 'Другое').map(t => {
                    const sel = recForm.types.includes(t);
                    return (
                      <div key={t} onClick={() => { setRecForm(f => ({ ...f, types: toggleType(f.types, t) })); setFormErrors(p => ({ ...p, recType: false })); }} style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: sel ? 'var(--accent-dim)' : 'var(--bg-primary)',
                        color: sel ? 'var(--accent)' : 'var(--text-secondary)',
                        border: '1px solid ' + (sel ? 'var(--accent)' : formErrors.recType ? 'var(--error)' : 'var(--border)'),
                      }} >{sel && <Check size={12} />}{t}</div>
                    );
                  })}
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 12, color: formErrors.recCity ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Город <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.city} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, city: v })); setFormErrors(p => ({ ...p, recCity: false })); setRecCitySuggestions(suggestCities(v)); }} onBlur={() => setTimeout(() => setRecCitySuggestions([]), 150)} placeholder="Berlin, München..." style={{ ...inputStyle, borderColor: formErrors.recCity ? 'var(--error)' : undefined }} />
                {recCitySuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: 10, marginTop: 4, overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    {recCitySuggestions.map(c => (
                      <div key={c} onPointerDown={(e) => {
                        e.preventDefault();
                        setRecForm(f => ({ ...f, city: c }));
                        setFormErrors(p => ({ ...p, recCity: false }));
                        setRecCitySuggestions([]);
                      }} style={{
                        padding: '10px 14px', fontSize: 14, color: 'var(--text-primary)',
                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid var(--border)',
                      }}>
                        <span>{c}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{findPlz(c)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 12, color: formErrors.recName ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Имя контакта <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.contactName} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, contactName: v })); setFormErrors(p => ({ ...p, recName: false })); }} placeholder="Иван Петров" style={{ ...inputStyle, borderColor: formErrors.recName ? 'var(--error)' : undefined }} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: formErrors.recPhone ? 'var(--error)' : 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Телефон <span style={{ color: 'var(--error)' }}>*</span></label>
                <input value={recForm.phone} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, phone: v })); setFormErrors(p => ({ ...p, recPhone: false })); }} placeholder="+49 176 12345678" type="tel" style={{ ...inputStyle, borderColor: formErrors.recPhone ? 'var(--error)' : undefined }} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Telegram @username</label>
                <input value={recForm.telegram} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, telegram: v })); }} placeholder="@username" style={inputStyle} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Краткий комментарий</label>
                <textarea value={recForm.comment} onChange={e => { const v = e.target.value; setRecForm(f => ({ ...f, comment: v })); }} placeholder="Ищет маляра на квартиру, бюджет ~5000€" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <button disabled={submitting} onClick={handleRecSubmit} style={{
                background: submitting ? 'var(--text-tertiary)' : 'var(--accent)', color: '#0D0D0D', border: 'none', borderRadius: 12,
                padding: '14px', fontSize: 16, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', marginTop: 8,
                opacity: submitting ? 0.6 : 1, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
              }}>{submitting ? 'Отправка...' : 'Опубликовать'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox
          NB: у <img> ниже maxWidth в % (не vw) — в десктопной колонке 95vw вылезало бы
          за её ширину; на мобильном оверлей = вся ширина экрана, картинка идентична. */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column',
        }}>
          {/* Top bar with close button */}
          <div onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            padding: '8px 12px',
            paddingTop: 'calc(var(--tg-safe-top, 0px) + 8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div onClick={(e) => { e.stopPropagation(); setLightbox(null); }} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 12,
              background: 'rgba(255,255,255,0.12)',
              cursor: 'pointer', color: '#fff', fontSize: 15, fontWeight: 600,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}>
              <ChevronLeft size={18} />
              Назад
            </div>
            {lightbox.photos.length > 1 && (
              <div style={{
                padding: '10px 16px', borderRadius: 12,
                background: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 600,
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              }}>
                {lightbox.index + 1} / {lightbox.photos.length}
              </div>
            )}
          </div>

          {/* Navigation arrows */}
          {lightbox.photos.length > 1 && lightbox.index > 0 && (
            <div onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index - 1 })); }} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 24, color: '#fff', zIndex: 10,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}><ChevronLeft size={22} /></div>
          )}
          {lightbox.photos.length > 1 && lightbox.index < lightbox.photos.length - 1 && (
            <div onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, index: lb.index + 1 })); }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 24, color: '#fff', zIndex: 10,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            }}><ChevronRight size={22} /></div>
          )}

          <img onClick={e => e.stopPropagation()}
            onTouchStart={e => { lbTouchX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              const dx = e.changedTouches[0].clientX - lbTouchX.current;
              if (Math.abs(dx) > 50) {
                if (dx < 0 && lightbox.index < lightbox.photos.length - 1) setLightbox(lb => ({ ...lb, index: lb.index + 1 }));
                if (dx > 0 && lightbox.index > 0) setLightbox(lb => ({ ...lb, index: lb.index - 1 }));
              }
            }}
            src={lightbox.photos[lightbox.index]} alt=""
            style={{ maxWidth: '95%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}
